import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { TramActividadService } from '../../../../services/database/tramActividad.service';
import { IGetTramActividadInfo, IGetUsuariosValidos, TramSeguimientoDto } from '../../../../shared/interfaces/database/tramite';
import { AccessRole, DEFAULT_DATE_FORMAT, FormGroupErrorMessageComponent, IFormOptions, LoadingPanelService, LocalService, noWhitespaceValidator, Utils } from 'lib-base';
import { TramSeguimientoService } from '../../../../services/database/tramSeguimiento.service';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TramRegistroService } from '../../../../services/database/tramRegistro.service';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom, forkJoin } from 'rxjs';
import { AdminActividadComponent } from '../admin-actividad.component';
import { Router } from '@angular/router';
import { ActividadFormularioComponent } from './actividad-formulario.component';

@Component({
  selector: 'app-actividad',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, FormsModule, TranslateModule,
    ButtonModule, InputTextareaModule, FileUploadModule, DropdownModule, ConfirmDialogModule, DialogModule,
    FormGroupErrorMessageComponent, TooltipModule, ActividadFormularioComponent
  ],
  providers: [ConfirmationService],
  templateUrl: './actividad.component.html',
  styleUrls: ['./actividad.component.scss']
})
export class ActividadComponent implements OnInit {

  @Input() actividadId !: number;
  @Input() tramRegistroId !: number;

  actividad !: IGetTramActividadInfo;
  usuariosValidos: IGetUsuariosValidos[] = [];
  selectedUsuarioIdValido !: number;

  readonly defaultDateFormat = DEFAULT_DATE_FORMAT;
  role = AccessRole;

  displayRechazarModal = false;
  rechazarForm !: FormGroup;
  private _finalizarSeguimiento: boolean = false;

  trans: any = {};
  private readonly parent: AdminActividadComponent = inject(AdminActividadComponent);
  @ViewChild(ActividadFormularioComponent) actividadFormulario !: ActividadFormularioComponent;

  readonly options: IFormOptions= {
      isDesigner: false,
      enableSubmit: true
  };

  constructor(
    private readonly _tas: TramActividadService, private readonly _ts: TramSeguimientoService, private readonly _tr: TramRegistroService, public ls: LocalService, private readonly _router: Router,
    private readonly _ms: MessageService, private readonly _fb: FormBuilder, private readonly _cs: ConfirmationService, private readonly _lps: LoadingPanelService, private readonly _t: TranslateService
  ) {
    this.configureRechazarForm();
  }

  ngOnInit() {
    this.translate();
    this.getActividadInfo();
    this.getUsuariosValidosParaActividad();
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      requiereRolSeguridad: this._t.get('msg.requiere-rol-seguridad'),
      actividad: this._t.get('common.actividad'),
      dependencia: this._t.get('common.dependencia'),
      ciudadano: this._t.get('common.ciudadano'),
      radicado: this._t.get('common.radicado'),
      orden: this._t.get('common.orden'),
      fechaInicioPlanificada: this._t.get('msg.fechaFinalizacionReal'),
      fechaCierrePlanificada: this._t.get('msg.fechaCierrePlanificada'),
      fechaInicioReal: this._t.get('msg.fechaInicioReal'),
      fechaFinalizacionReal: this._t.get('msg.fechaFinalizacionReal'),
      estadoDeLaActividad: this._t.get('msg.estadoDeLaActividad'),
      usuarioAsignado: this._t.get('common.usuarioAsignado'),
      seguimiento: this._t.get('common.seguimiento'),
      documento: this._t.get('common.documento'),
      guardarSeguimiento: this._t.get('common.guardarSeguimiento'),
      finalizarActividad: this._t.get('common.finalizarActividad'),
      reabrirActividad: this._t.get('common.reabrirActividad'),
      rechazarTramite: this._t.get('common.rechazarTramite'),
      observaciones: this._t.get('common.observaciones'),
      completeCamposRequeridos: this._t.get('msg.completeCamposRequeridos'),
      advertencia: this._t.get('msg.advertencia'),
      alFinalizarActividadMensaje: this._t.get('msg.alFinalizarActividadMensaje')
    }));
  }

  private configureRechazarForm() {
    this.rechazarForm = this._fb.group({
      observaciones: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(255)]],
    });
  }

  getActividadInfo() {
    if (!this.actividadId) return;
    this._tas.getActividadInfo(this.actividadId).subscribe({
      next: (data: IGetTramActividadInfo) => {
        this.actividad = data;
        this.parent.requierePago = data.requierePago;
        this.parent.empresaId = data.portalUsuarioEmpresaId;
        this.selectedUsuarioIdValido = data.usuarioAsignadoId;
        this.parent.title = data.tiposTramiteDescripcion;
        this.actividadFormulario.loadForm(data.formularioId, data.formularioVersion);
      }
    });
  }

  getUsuariosValidosParaActividad() {
    this._tas.getUsuariosValidosParaActividad(this.actividadId).subscribe({
      next: (usuarios: IGetUsuariosValidos[]) => {
        this.usuariosValidos = usuarios;
      }
    });
  }

  //#region Finalizar Seguimiento

  onSubmitForm = (data: string) => {
    const seguimiento = {
        formularioRespuesta: data,
      } as TramSeguimientoDto;

    this._lps.show(true);
    if (this._finalizarSeguimiento) {
      this._tas.finalizarActividad(this.actividadId, seguimiento).subscribe({
        next: (siguienteActividad: number) => {
          this._lps.hide();
          if (siguienteActividad === -1) {
            this._router.navigate(['/configuracion/landing']);
          } else {
            this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
              this._router.navigate(['/configuracion/actividad/', this.tramRegistroId, siguienteActividad]);
            });
          }

        }
      });
    } else {
      this._ts.createSeguimiento(this.actividadId, seguimiento).subscribe({
        next: (response: string) => {
          this._ms.add({ severity: 'success', summary: 'Exito', detail: response });
          this.actividadFormulario.clearPage();
          this._lps.hide();
        }
      });
    }
  };

  onSubmitSeguimiento(event: Event) {
    this._finalizarSeguimiento = false;
    this.actividadFormulario.submit();
  }

  finalizarActividad(event: Event) {
    if(this.actividad.insercionDinamica){
      this._cs.confirm({
        target: event.target as EventTarget,
        header: this.trans.finalizarActividad,
        message: this.trans.alFinalizarActividadMensaje,
        accept: () => {
          this._finalizarSeguimiento = true;
          this.actividadFormulario.submit();
        },
        reject: () => {
        }
      });
    }else{
      this._finalizarSeguimiento = true;
      this.actividadFormulario.submit();
    }

  }

  //#endregion Finalizar Seguimiento
  reasignarActividad(usuarioId: number) {
    this._lps.show(true);
    this._tas.reasignarActividad(this.actividadId, usuarioId).subscribe({
      next: (response: string) => {
        this._ms.add({ severity: 'success', summary: 'Exito', detail: response });
        this.getActividadInfo();
        this._lps.hide();
      }
    });
  }

  reabrirActividad() {
    this._lps.show(true);
    this._tas.reabrirActividad(this.actividadId).subscribe({
      next: (response: string) => {
        this._ms.add({ severity: 'success', summary: 'Exito', detail: response });
        this.getActividadInfo();
        this._lps.hide();
      }
    });
  }

  reabrirActividadConfirm(event: MouseEvent) {
    this._cs.confirm({
      target: event.target as EventTarget,
      header: "Reabrir Actividad",
      message: 'Estas seguro de que quieres reabrir la actividad?',
      accept: () => {
        this.reabrirActividad();
      },
      reject: () => {

      }
    });
  }

  onUsuarioValidoChange(event: DropdownChangeEvent) {
    this._cs.confirm({
      header: "Reasignar Actividad",
      target: event.originalEvent.target as EventTarget,
      message: 'Estas seguro de que quieres reasignar la actividad?',
      accept: () => {
        this.reasignarActividad(event.value);
      },
      reject: () => {
        this.selectedUsuarioIdValido = this.actividad.usuarioAsignadoId;
      }
    });
  }

  openRespuesta(tramRegistroId: number): void {
    const url = `#/configuracion/formulario/view-respuesta/${tramRegistroId}`;
    window.open(url, '_blank');
  }

  //#region  Rechazar
  resetRechazarForm() {
    this.rechazarForm.reset();
  }

  openRechazarModal() {
    this.resetRechazarForm();;
    this.displayRechazarModal = true;
  }

  private rechazarTramite(observaciones: string) {
    this._lps.show(true);
    this._tr.rechazarTramite(this.actividad.tramRegistroId, observaciones).subscribe({
      next: (response: string) => {
        this._ms.add({ severity: 'success', summary: 'Exito', detail: response });
        this.displayRechazarModal = false;
        this._lps.hide();
        this._router.navigate(['/configuracion/landing']);
      }
    });
  }

  onSubmitRechazarForm() {
    if (this.rechazarForm.invalid) {
      Utils.markAllFormControlsAsTouched(this.rechazarForm);
      this._ms.add({ severity: 'warn', summary: this.trans.advertencia, detail: this.trans.completeCamposRequeridos });
      return;
    }

    this.rechazarTramite(this.rechazarForm.get('observaciones')?.value);
  }

  //#endregion Rechazar
}
